import * as React from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import useNotification from '../notification/notificationContext';
import { NOTIFICATIONS } from '../../config';


export const InvoicesContext = React.createContext();

function useInvoices() {
  const context = React.useContext(InvoicesContext);
  if (!context) throw new Error('useInvoices must be used within a InvoicesProvider');

  const [invoicesState, dispatch] = context;
  
  const { setNotification } = useNotification();

  const getAllInvoices = () => {
    return axios.get(`${API_BASE_URL}/invoice/get-all`).then(res => {
      const response = res.data;
      return response;
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: err.response?.data?.message || 'We were unable to retrieve invoices from the server. Please try again later.'
      })
      throw err;
    })
  }

  const getUserInvoices = (userId) => {
    return axios.get(`${API_BASE_URL}/invoice/user/${userId}`).then(res => {
      const response = res.data;
      return response;
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: err.response?.data?.message || 'We were unable to retrieve invoices from the server. Please try again later.'
      })
      throw err;
    })
  }

  const uploadInvoice = (invoiceFile, invoiceData) => {
    var formData = new FormData();
    formData.append("invoice", invoiceFile);
    formData.append("invoiceDate", invoiceData.invoiceDate);
    formData.append("invoiceAmount", invoiceData.amount);

    return axios.post(`${API_BASE_URL}/invoice/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }).then(res => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.SUCCESS,
        title: 'Success',
        message: 'Invoice uploaded successfully.'
      })
      const response = res.data;
      return response;
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: err.response?.data?.error?.message || 'We were unable to retrieve invoices from the server. Please try again later.'
      })
      throw err;
    })
  }

  const getInvoiceDocument = (invoiceId) => {
    return  axios.get(`${API_BASE_URL}/file/invoice/${invoiceId}`).then(res => {
      const response = res.data;
      return response;
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: err.response?.data?.message || 'Unable to retrieve invoice document'
      })
      throw err;
    })
  };

  const downloadInvoice = (invoiceId, fileName) => {
    axios.get(`${API_BASE_URL}/file/invoice/${invoiceId}/${fileName}`).then(res => {
      const downloadUrl = res.data.data;
      const newWindow = window.open(downloadUrl, '_blank', 'noopener,noreferrer');
      if (newWindow) newWindow.opener = null;
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: err.response?.data?.message || 'Unable to download file'
      })
      throw err;
    })
  };

  const getInvoiceDetails = (id) => {
    return axios.get(`${API_BASE_URL}/invoice/get-details/${id}`).then(res => {
      return res.data;
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: 'We were unable to get invoice details. Please try again later.'
      })
      throw err;
    })
  }

  const updateInvoiceStatus = (id, status) => {
    return axios.patch(`${API_BASE_URL}/invoice/${id}`, { data: status }).then(res => {
      const response = res.data;
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.SUCCESS,
        title: 'Success',
        message: 'Invoice status updated successfully.'
      })
      return response;
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: 'We were unable to edit the invoice status. Please try again later.'
      })
      throw err;
    })
  }

  const deleteInvoice = (invoiceId) => {
    return axios.delete(`${API_BASE_URL}/invoice/${invoiceId}`).then(res => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.SUCCESS,
        title: 'Success',
        message: 'Invoice deleted successfully.'
      });
      return res;
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: err.response?.data?.message || 'Unable to delete invoice'
      })
      throw err;
    })
  };

  return {
    invoicesState,
    dispatch,
    getAllInvoices,
    uploadInvoice,
    getUserInvoices,
    getInvoiceDetails,
    getInvoiceDocument,
    updateInvoiceStatus,
    deleteInvoice,
    downloadInvoice
  }
}

export default useInvoices